import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, ErrorState, Skeleton, PrimaryButton } from "@/components/BookingShell";
import { formatFullDate } from "@/lib/format";

export function TimeStep({ setFooter }) {
    const { service, date, time, setTime, next, cart } = useBooking();
    const [slots, setSlots] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!service?.id || !date) return;
        setError(null);
        setSlots(null);
        if (service?.cartQuote) api.cartAvailability(cart.map((c) => ({service_id:c.service.id, quantity:c.quantity})), date, date).then((r) => setSlots(r.slots_by_date[date] || [])).catch(setError);
        else api.listSlots({ serviceId: service.id, date }).then((r) => setSlots(r.slots || [])).catch(setError);
    }, [service, date, cart]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        setFooter(
            time && slots?.some((s) => s.time === time) ? (
                <PrimaryButton onClick={next} testId="time-continue-button">Continue</PrimaryButton>
            ) : null
        );
        return () => setFooter(null);
    }, [time, slots, next, setFooter]);

    return (
        <div>
            <StepTitle
                eyebrow={formatFullDate(date)}
                title={<>Choose a <em className="italic gold-text not-italic font-serif">time</em></>}
                subtitle="Live availability from our calendar. Times refresh as bookings occur."
                testId="time-step-title"
            />

            {error && <ErrorState error={error} onRetry={load} />}

            {!slots && !error && (
                <div className="grid grid-cols-3 gap-3" data-testid="time-skeleton">
                    {Array.from({ length: 9 }).map((_, k) => (
                        <Skeleton key={k} className="h-12" />
                    ))}
                </div>
            )}

            {slots && slots.length === 0 && (
                <div className="rounded-2xl glass p-8 text-center" data-testid="time-empty-state">
                    <div className="font-serif text-2xl">Fully booked</div>
                    <p className="mt-2 text-sm text-white/55">There are no available times on this date. Please choose another day.</p>
                </div>
            )}

            {slots && slots.length > 0 && (
                <div className="grid grid-cols-3 gap-3 animate-fade-up" data-testid="time-list">
                    {slots.map((s) => {
                        const active = time === s.time;
                        return (
                            <button
                                key={s.iso}
                                onClick={() => setTime(s.time)}
                                data-testid={`time-slot-${s.time}`}
                                className={`h-12 rounded-full border font-ui text-sm font-semibold tap-scale transition-all
                                    ${active
                                        ? "bg-gold text-navy border-gold shadow-[0_10px_20px_-10px_rgba(197,160,89,0.7)]"
                                        : "bg-white/[0.06] border-white/25 text-white hover:bg-white/[0.10] hover:border-gold hover:text-gold"}`}
                            >
                                {s.time}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
